import { Link } from 'react-router-dom'
import { ArrowRight, MapPin, Clock, Search } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState } from 'react'

const Career = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tümü')

  const categories = ['Tümü', 'Pazarlama', 'Tasarım', 'Teknoloji', 'Satış']

  const jobs = [
    {
      id: 1,
      title: 'Dijital Pazarlama Uzmanı',
      department: 'Pazarlama',
      location: 'İstanbul / Hibrit',
      type: 'Tam Zamanlı',
      description: 'Google Ads ve Meta Ads platformlarında kampanya yönetimi ve optimizasyonu yapacak, ROI odaklı çalışacak deneyimli pazarlama uzmanı arıyoruz.',
      requirements: [
        'Google Ads ve Meta Ads deneyimi',
        'Analitik düşünme yeteneği',
        'İletişim becerileri',
        'Minimum 2 yıl deneyim'
      ],
      postedDate: '2 gün önce'
    },
    {
      id: 2,
      title: 'UI/UX Tasarımcı',
      department: 'Tasarım',
      location: 'İstanbul / Ofis',
      type: 'Tam Zamanlı',
      description: 'Modern ve kullanıcı odaklı web ve mobil uygulama tasarımları yapacak, yaratıcı tasarımcı ekibimize katılacak deneyimli tasarımcı arıyoruz.',
      requirements: [
        'Figma, Sketch veya Adobe XD deneyimi',
        'Portföy gereklidir',
        'Kullanıcı deneyimi odaklı düşünme',
        'Minimum 3 yıl deneyim'
      ],
      postedDate: '1 hafta önce'
    },
    {
      id: 3,
      title: 'Frontend Developer',
      department: 'Teknoloji',
      location: 'İstanbul / Uzaktan',
      type: 'Tam Zamanlı',
      description: 'React, Next.js ve TypeScript kullanarak modern web uygulamaları geliştirecek, performans ve kullanıcı deneyimine odaklanacak frontend developer arıyoruz.',
      requirements: [
        'React ve Next.js deneyimi',
        'TypeScript bilgisi',
        'Git versiyon kontrolü',
        'Minimum 2 yıl deneyim'
      ],
      postedDate: '3 gün önce'
    },
    {
      id: 4,
      title: 'Sosyal Medya Yöneticisi',
      department: 'Pazarlama',
      location: 'İstanbul / Hibrit',
      type: 'Tam Zamanlı',
      description: 'Sosyal medya stratejileri geliştirecek, içerik üretimi yapacak ve topluluk yönetimi gerçekleştirecek sosyal medya yöneticisi arıyoruz.',
      requirements: [
        'Sosyal medya platformları deneyimi',
        'İçerik üretimi becerileri',
        'Yaratıcı düşünme',
        'Minimum 1 yıl deneyim'
      ],
      postedDate: '5 gün önce'
    },
    {
      id: 5,
      title: 'Backend Developer',
      department: 'Teknoloji',
      location: 'İstanbul / Uzaktan',
      type: 'Tam Zamanlı',
      description: 'Node.js ve Python kullanarak ölçeklenebilir backend sistemleri geliştirecek, API tasarımı yapacak backend developer arıyoruz.',
      requirements: [
        'Node.js veya Python deneyimi',
        'RESTful API tasarımı',
        'Veritabanı yönetimi',
        'Minimum 3 yıl deneyim'
      ],
      postedDate: '1 hafta önce'
    },
    {
      id: 6,
      title: 'Dijital Pazarlama Stratejisti',
      department: 'Pazarlama',
      location: 'İstanbul / Ofis',
      type: 'Tam Zamanlı',
      description: 'Dijital pazarlama stratejileri geliştirecek, müşteri kampanyalarını yönetecek ve ROI analizleri yapacak stratejist arıyoruz.',
      requirements: [
        'Dijital pazarlama deneyimi',
        'Stratejik düşünme',
        'Veri analizi',
        'Minimum 4 yıl deneyim'
      ],
      postedDate: '4 gün önce'
    }
  ]

  const filteredJobs = selectedCategory === 'Tümü' 
    ? jobs 
    : jobs.filter(job => job.department === selectedCategory)

  return (
    <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
      
      {/* Hero Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 mb-6">
              Kariyer
            </span>
            <h1 className="text-[28px] md:text-[44px] font-bold text-[#111] leading-tight mb-6">
              Thinklyze Ekibine Katılın
            </h1>
            <p className="text-lg md:text-xl text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              Dijital pazarlama dünyasında fark yaratmak isteyen, tutkulu ve yaratıcı profesyoneller arıyoruz. 
              Birlikte büyüyelim ve başarı hikayeleri yazalım.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Job Listings Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-white text-[#555] border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Jobs Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#111] mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="flex items-center gap-2 text-[#666] text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#666] text-sm">
                        <Clock className="w-4 h-4" />
                        <span>{job.type}</span>
                      </div>
                    </div>
                    <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-4">
                      {job.department}
                    </span>
                  </div>
                  <span className="text-xs text-[#999]">{job.postedDate}</span>
                </div>
                
                <p className="text-[#555] leading-relaxed mb-6">{job.description}</p>
                
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-[#111] mb-3">Gereksinimler:</h4>
                  <ul className="space-y-2">
                    {job.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-[#555]">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center w-full bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200"
                >
                  Başvur
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#111] mb-2">Pozisyon bulunamadı</h3>
              <p className="text-[#555]">Seçtiğiniz kategoride şu an açık pozisyon bulunmamaktadır.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24 relative border-t border-gray-200">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-[24px] md:text-[36px] font-bold text-[#111] leading-tight mb-6">
              Aradığınız pozisyonu bulamadınız mı?
            </h2>
            <p className="text-lg text-[#555] mb-8 leading-relaxed">
              Özgeçmişinizi gönderin, uygun pozisyon açıldığında sizinle iletişime geçelim.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-black text-white px-8 py-4 rounded-full text-[15px] font-medium hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Özgeçmiş Gönder
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Career














